import { useEffect, useState } from "react";
import { Upload, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, DataState, NativeSelect, Field, ConfirmDialog } from "../components/common";
import { DocRow, useDocumentDelete } from "../components/DocumentsPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TYPE_LABEL = { worker: "Operai", site: "Cantieri", vehicle: "Mezzi" };

function entityLabel(x) {
  if (x.full_name) return x.full_name;
  return x.code ? `${x.code} · ${x.name}` : x.label;
}

function useEntityOptions(open, type) {
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState("");
  useEffect(() => {
    if (!open) return;
    const url = { worker: "/workers", site: "/sites", vehicle: "/vehicles" }[type];
    api.get(url).then((r) => {
      const items = r.data.items.map((x) => ({ id: x.id, label: entityLabel(x) }));
      setEntities(items);
      setEntityId(items[0]?.id || "");
    });
  }, [open, type]);
  return { entities, entityId, setEntityId };
}

function useDocumentUploadForm({ open, categories, types, onOpenChange, onDone }) {
  const [type, setType] = useState(types[0]);
  const [cat, setCat] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ent = useEntityOptions(open, type);
  useEffect(() => {
    if (open) setCat(categories[type][0]);
  }, [open, type, categories]);
  const submit = async () => {
    if (!file || !ent.entityId) return toast.error("Seleziona elemento e file");
    const fd = new FormData();
    fd.append("entity_type", type);
    fd.append("entity_id", ent.entityId);
    fd.append("category", cat);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/documents", fd);
      toast.success("✓ Documento caricato");
      onOpenChange(false);
      setFile(null);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { type, setType, cat, setCat, setFile, busy, submit, ...ent };
}

function UploadDialog({ open, onOpenChange, categories, types, onDone }) {
  const f = useDocumentUploadForm({ open, categories, types, onOpenChange, onDone });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover" data-testid="doc-upload-dialog">
        <DialogHeader><DialogTitle>Carica documento</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Archivio"><NativeSelect value={f.type} onChange={(e) => f.setType(e.target.value)} data-testid="upload-type-select">{types.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}</NativeSelect></Field>
          <Field label="Elemento"><NativeSelect value={f.entityId} onChange={(e) => f.setEntityId(e.target.value)} data-testid="upload-entity-select">{f.entities.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</NativeSelect></Field>
          <Field label="Categoria"><NativeSelect value={f.cat} onChange={(e) => f.setCat(e.target.value)} data-testid="upload-category-select">{categories[f.type].map((c) => <option key={c}>{c}</option>)}</NativeSelect></Field>
          <Field label="File (PDF, immagini, max 15 MB)"><Input type="file" onChange={(e) => f.setFile(e.target.files[0])} data-testid="upload-file-input" /></Field>
          <Button className="w-full" onClick={f.submit} disabled={f.busy} data-testid="upload-submit-btn">{f.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" />Carica</>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const UPLOAD_TYPES = { admin: ["site", "worker", "vehicle"], responsabile: ["site"] };
const canDeleteDoc = (role, d) => role === "admin" || (role === "responsabile" && d.entity_type === "site");

function DocumentsFilters({ role, q, setQ, type, setType }) {
  return (
    <div className="surface p-3 mb-4 grid gap-2 sm:grid-cols-[1fr_220px]">
      <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input className="pl-9 h-10" placeholder="Cerca documento…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="documents-search-input" /></div>
      {role !== "operaio" && (
        <NativeSelect value={type} onChange={(e) => setType(e.target.value)} data-testid="documents-type-filter">
          <option value="">Tutti gli archivi</option>{Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </NativeSelect>
      )}
    </div>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data, loading, reload } = useApi("/documents", { entity_type: type, q });
  const { del, setDel, remove } = useDocumentDelete(reload);
  const uploadTypes = UPLOAD_TYPES[user.role] || [];
  const canUpload = uploadTypes.length > 0 && Boolean(data);
  const title = user.role === "operaio" ? "I miei documenti" : "Documenti";
  return (
    <div>
      <PageHeader eyebrow="Archivio" title={title} subtitle="Attestati, patentini, idoneità, PSC, POS, contratti, libretti, assicurazioni e revisioni"
        actions={canUpload && <Button onClick={() => setOpen(true)} data-testid="documents-upload-btn"><Upload className="h-4 w-4 mr-2" />Carica documento</Button>} />
      <DocumentsFilters role={user.role} q={q} setQ={setQ} type={type} setType={setType} />
      <div className="surface p-4">
        <DataState loading={loading && !data} empty={!data?.items?.length} emptyTitle="Nessun documento">
          <ul className="divide-y divide-border/60" data-testid="documents-list">{(data?.items || []).map((d) => <DocRow key={d.id} d={d} showEntity canDelete={canDeleteDoc(user.role, d)} onDelete={setDel} />)}</ul>
        </DataState>
      </div>
      {canUpload && <UploadDialog open={open} onOpenChange={setOpen} categories={data.categories} types={uploadTypes} onDone={reload} />}
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title="Eliminare il documento?" description={del?.name} onConfirm={remove} />
    </div>
  );
}
